import React from "react";
import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { LinearProgress, Box, Tooltip, IconButton } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import { useSprayingWindow } from '@graniot/common/adapters/spraying';
import Paper from '@material-ui/core/Paper';

import { format, parse } from 'date-fns';

import { useForecast } from '@graniot/common/adapters/aemet';
import SkyImage from "../SkyImage";
import DailyForecast from "./DailyForecast";
import HourlyForecast from "./HourlyForecast";
import { ErrorPage } from "../../../../ErrorPage";
import { InputForm } from "../../advanced/InputForm";
import { ProviderSelector } from "./ProviderSelector";


const useStyles = makeStyles((theme) => ({
    divider: {
        margin: theme.spacing(2),
    },
    slider: {
        width: 300,
        marginRight: theme.spacing(2),
    },
    pickers: {
        marginTop: theme.spacing(2),
    },
    avatar: {
        marginRight: theme.spacing(1),
    },
    root: {
        width: '100%',
        margin: 2.5,
    },
    image: {
        height: 17,
        marginRight: 2,
        marginLeft: 4
    }
}));

var initialParameters = {
    uTemp: 30,
    uPrec: 0,
    uMInfWind: 3,
    uMSupWind: 7,
    uHInfWind: 2,
    uHSupWind: 10,
    modify: false
};

export const Forecast = ({ parcel }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [expanded, setExpanded] = React.useState(false);
    const [forecastDayDetail, setForecastDayDetail] = React.useState(null);
    const [parameters, setParameters] = React.useState(initialParameters);
    const [providers, setProviders] = React.useState([
        { name: 'Aemet', value: 'AEMET' },
        { name: 'Open Weather', value: 'OPEN_WEATHER' }
    ]);
    const [selectedProvider, setSelectedProvider] = React.useState(providers[1]);


    const handleDetailClick = (day) => {
        setForecastDayDetail(day);
    };

    const { data: forecastData, refetch: forecastRefetch } = useForecast(parcel.id, selectedProvider.value);
    const { data: sprayingData, refetch: sprayingRefetch } = useSprayingWindow(parcel.id, parameters.uPrec, parameters.uMInfWind, parameters.uMSupWind, parameters.uHInfWind, parameters.uHSupWind, parameters.uTemp, parameters.modify);

    React.useEffect(() => {
        forecastRefetch();
    }, [parcel, selectedProvider]);

    React.useEffect(() => {
        if (forecastData && forecastData?.status !== 'error')
            if (Object.keys(forecastData).length === 0 || (
                Object.keys(forecastData?.current).length === 0
                && forecastData?.daily.length === 0
                && forecastData?.hourly.length === 0)) {
                setSelectedProvider(providers[1])
                setProviders([providers[1]])
            }
    }, [forecastData]);

    React.useEffect(() => {
        setExpanded(true);
    }, [forecastDayDetail]);

    React.useEffect(() => {
        if (sprayingData) {
            setParameters({
                ...sprayingData['parameters'],
                modify: 'false',
            })
        }
    }, [sprayingData]);

    const handleInputChange = (event) => {
        event.preventDefault();
        if (selectedProvider.value == 'AEMET') {
            let { name, value } = event.target;

            if (value == '') {
                value = 0
            }

            setParameters({
                ...parameters,
                [name]: value,
                modify: 'true',
            });

            if (sprayingData) {
                sprayingRefetch();
            }
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
    };

    const handleSelectedProvider = (event, newValue) => {
        setSelectedProvider(newValue);
    };

    if (!forecastData) {
        return <LinearProgress></LinearProgress>;
    }

    if ("status" in forecastData && forecastData.status === 'error') {
        return (
            <React.Fragment>
                <Grid container spacing={3}>
                    <Grid item style={{ marginBottom: 15, marginRight: 10 }} xs={9} sm={4}>
                        <ProviderSelector providers={providers} selectedProvider={selectedProvider} handleSelectedProvider={handleSelectedProvider} />
                    </Grid>
                    <Grid item xs={1} sm={2}>
                        <Tooltip title={<p style={{ fontSize: '1.2em', color: 'whitesmoke' }}>{t("IfapaData.FormInfo")}</p>} placement="top" arrow>
                            <IconButton>
                                <HelpIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Card style={{ padding: 20, margin: 20 }}>
                    <Typography variant="h6" color="error">
                        {t(forecastData.message)}
                    </Typography>
                </Card>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Grid container spacing={3}>
                <Grid item style={{ marginBottom: 15, marginRight: 10 }} xs={9} sm={4}>
                    <ProviderSelector providers={providers} selectedProvider={selectedProvider} handleSelectedProvider={handleSelectedProvider} />
                </Grid>
                <Grid item xs={1} sm={2}>
                    <Tooltip title={<p style={{ fontSize: '1.2em', color: 'whitesmoke' }}>{t("IfapaData.FormInfo")}</p>} placement="top" arrow>
                        <IconButton>
                            <HelpIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            {forecastData.current && Object.keys(forecastData.current).length !== 0 && <Card style={{ marginBottom: 30 }} elevation={10}>
                <CardHeader
                    title={`${forecastData.formatted}`}
                    subheader={format(new Date(), 'PP | cccc, hh:mm')}
                    style={{ padding: 25 }}
                />
                <CardContent style={{ paddingLeft: 35 }}>
                    <Grid container
                        spacing={2}
                        alignitems="flex-start"
                        direction='row'
                        className={classes.root}
                    >
                        <Grid container xs={12} sm={7} md={7} lg={7} justify="start" >
                            <Grid item className={classes.avatar}><SkyImage imageHeight={110} skyState={forecastData.current.detailed_status} /></Grid>
                            <Grid item><Typography variant="h1">{Math.floor(forecastData.current.temp.temp)}°C</Typography></Grid>
                        </Grid >
                        <Grid container xs={12} sm={5} md={5} lg={5} justify="start" >
                            <Grid item>
                                {forecastData.daily[0].temp && <Box style={{ color: "dimGrey", fontSize: '1.2rem' }} display="block" justifyContent="center" p={1}>

                                    {t("Weather.max")} {Math.floor(forecastData.daily[0].temp.max)}ºC |


                                    {t("Weather.min")} {Math.floor(forecastData.daily[0].temp.min)}ºC

                                </Box>}
                                <Box style={{ color: "dimGrey", fontSize: '1.1rem' }} display="block" justifyContent="center" p={1}>
                                    {t("Weather.feelslike")} {Math.floor(forecastData.current.temp.feels_like)}ºC
                                </Box>
                                {forecastData.current.humidity && <Box style={{ color: "dimGrey", fontSize: '1.1rem' }} display="block" justifyContent="center" p={1}>
                                    {t("Weather.hum")} {forecastData.current.humidity} %
                                </Box>}
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
                <CardContent style={{ paddingLeft: 35 }}>
                    <Typography variant="h6" color="textSecondary" component="p">
                        {forecastData.current.detailed_status}
                    </Typography>
                    <Typography paragraph>{Math.floor(forecastData.current.precipitation_probability * 100)} {t("Weather.precipitation_probability")}</Typography>
                </CardContent>
                <CardContent>
                    <Paper style={{ width: "100%", overflowX: 'auto' }}>
                        <DailyForecast data={forecastData.daily} handleDetailClick={handleDetailClick} forecastDayDetail={forecastDayDetail}></DailyForecast>
                    </Paper>
                </CardContent>
            </Card>}

            {forecastDayDetail && forecastData.hourly && <Grid container
                spacing={2}
                alignItems="flex-start"
                direction='row'
                className={classes.root}
            >

                <Grid item
                    xs={12}
                    sm={12}
                    md={(selectedProvider.value == 'AEMET' ? 8 : 12)}
                    lg={(selectedProvider.value == 'AEMET' ? 8 : 12)}
                ><Card style={{ marginBottom: 30 }} elevation={4}>
                        <CardHeader
                            style={{ padding: 30 }}
                            title={format(parse(forecastDayDetail.substring(0, 10), 'yyyy-MM-dd', new Date()), 'EEE d, LLL')}
                        />
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <CardContent style={{ padding: 5, background: '#F5F5F5' }}>
                                <Paper style={{ width: "100%", overflowX: 'auto' }}>
                                    {selectedProvider.value == 'AEMET' ? <HourlyForecast data={forecastData.hourly} forecastDayDetail={forecastDayDetail} sprayingData={sprayingData}></HourlyForecast> :
                                        <HourlyForecast data={forecastData.hourly} forecastDayDetail={forecastDayDetail}></HourlyForecast>}
                                </Paper>
                            </CardContent>
                        </Collapse>
                    </Card>
                </Grid>
                {selectedProvider.value == 'AEMET' ? <Grid item xs={12} sm={12} md={4} lg={4}>
                    <Card className={classes.root} elevation={4}>
                        <CardHeader
                            style={{ padding: 15, paddingBottom: 0 }}
                            titleTypographyProps={{ variant: 'subtitle1' }}
                            title={t("Weather.caption")}
                        />
                        <CardContent >
                            <Grid container justifyContent="flex-start">

                                <Grid item justifycontent='center'><InputForm inputValue={parameters.uTemp} handleChange={handleInputChange} handleSubmit={handleSubmit} hText="Límite de temperatura (ºC)" name="uTemp" /></Grid>
                                <Grid item justifycontent='center'><InputForm inputValue={parameters.uPrec} handleChange={handleInputChange} handleSubmit={handleSubmit} hText="Límite de precipitación (mm)" name="uPrec" /></Grid>
                                <Grid item justifycontent='center'><InputForm inputValue={parameters.uMInfWind} handleChange={handleInputChange} handleSubmit={handleSubmit} hText="Límite medio inf de viento (km/h)" name="uMInfWind" /></Grid>
                                <Grid item justifycontent='center'><InputForm inputValue={parameters.uMSupWind} handleChange={handleInputChange} handleSubmit={handleSubmit} hText="Límite medio sup de viento (km/h)" name="uMSupWind" /></Grid>
                                <Grid item justifycontent='center'><InputForm inputValue={parameters.uHInfWind} handleChange={handleInputChange} handleSubmit={handleSubmit} hText="Límite alto inferior de viento (km/h)" name="uHInfWind" /></Grid>
                                <Grid item justifycontent='center'><InputForm inputValue={parameters.uHSupWind} handleChange={handleInputChange} handleSubmit={handleSubmit} hText="Límite alto superior de viento (km/h)" name="uHSupWind" /></Grid>
                                <Grid item justifycontent='center' style={{ marginTop: 20, color: 'dimGrey' }}>
                                    {t("Weather.info")}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid> : null}
            </Grid>}
        </React.Fragment>
    )
}
